import { useEffect, useState } from 'react';
import { adminApi } from '../../utils/api';
import { Alert, Badge, Button, Card, Input, Modal, Spinner, Toggle, Select } from './shared';

const EMPTY = { id: '', name: '', description: '', color: 'slate', is_active: true };
const COLOR_OPTIONS = [
    { value: 'slate', label: 'slate' },
    { value: 'blue', label: 'blue' },
    { value: 'emerald', label: 'emerald' },
    { value: 'amber', label: 'amber' },
    { value: 'red', label: 'red' },
    { value: 'purple', label: 'purple' },
];

const BADGE_VARIANT = { red: 'danger', amber: 'warning', emerald: 'success', blue: 'info', purple: 'purple' };

export default function AdminEnvironments() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            setRows(await adminApi.listEnvironments(true));
            setError(null);
        } catch (e) { setError(e.message || 'Failed to load'); }
        finally { setLoading(false); }
    };

    const openCreate = () => { setEditing({ ...EMPTY }); setFormOpen(true); };
    const openEdit = (r) => { setEditing({ ...r }); setFormOpen(true); };

    const handleSave = async (payload) => {
        const isEditing = rows.some((r) => r.id === payload.id);
        if (isEditing) await adminApi.updateEnvironment(payload.id, payload);
        else await adminApi.createEnvironment(payload);
        setFormOpen(false);
        setSuccess(`Saved '${payload.id}'`);
        await load();
    };

    const handleSetDefault = async (id) => {
        try {
            await adminApi.setDefaultEnvironment(id);
            setSuccess(`'${id}' is now the default environment`);
            await load();
        } catch (e) { setError(e.message || 'Failed'); }
    };

    const handleDelete = async (id) => {
        if (!confirm(`Delete environment '${id}'? This cannot be undone.`)) return;
        try {
            await adminApi.deleteEnvironment(id);
            setSuccess(`Deleted '${id}'`);
            await load();
        } catch (e) { setError(e.message || 'Failed'); }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Environments</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Content variant axis for skills / knowledge / sub-agents. One row is the default — all resources without an explicit env land there.
                    </p>
                </div>
                <Button onClick={openCreate}>+ New environment</Button>
            </div>

            {error && <Alert variant="danger" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            {loading ? (
                <Spinner center text="Loading…" />
            ) : rows.length === 0 ? (
                <Card><div className="text-center py-8 text-sm text-gray-500">No environments yet</div></Card>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">ID</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Name</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Default</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Active</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-56">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map((r) => (
                                <tr key={r.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                        <Badge variant={BADGE_VARIANT[r.color] || 'outline'} dot>{r.id}</Badge>
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className="font-medium text-gray-900">{r.name}</span>
                                        {r.description && <p className="text-xs text-gray-400 mt-0.5">{r.description}</p>}
                                    </td>
                                    <td className="px-3 py-3">
                                        {r.is_default ? <Badge variant="success" dot>default</Badge> : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-3 py-3">
                                        {r.is_active ? <Badge variant="success">active</Badge> : <Badge variant="warning">inactive</Badge>}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {!r.is_default && (
                                                <Button variant="ghost" size="xs" onClick={() => handleSetDefault(r.id)}>Set default</Button>
                                            )}
                                            <Button variant="ghost" size="xs" onClick={() => openEdit(r)}>Edit</Button>
                                            <Button variant="ghost" size="xs" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(r.id)}>Delete</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <EnvironmentFormModal
                open={formOpen}
                onClose={() => setFormOpen(false)}
                initial={editing}
                isExisting={editing && rows.some((r) => r.id === editing.id)}
                onSave={handleSave}
            />
        </div>
    );
}

function EnvironmentFormModal({ open, onClose, initial, isExisting, onSave }) {
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (open) {
            setForm({ ...EMPTY, ...(initial || {}) });
            setErr('');
        }
    }, [open, initial]);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async () => {
        setErr('');
        if (!form.id || !form.name) {
            setErr('id and name are required');
            return;
        }
        setSubmitting(true);
        try {
            await onSave({
                ...form,
                description: form.description || null,
                color: form.color || null,
            });
        } catch (e) { setErr(e?.message || String(e)); }
        finally { setSubmitting(false); }
    };

    return (
        <Modal open={open} onClose={onClose} size="md" title={isExisting ? `Edit '${form.id}'` : 'New environment'}>
            <Modal.Body>
                {err && <Alert variant="danger">{err}</Alert>}
                <Input
                    label="ID *" hint="URL-safe slug, used directly in S3 path agent-resources/{id}/... — immutable after create"
                    value={form.id} disabled={isExisting}
                    onChange={(e) => set('id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    placeholder="development"
                />
                <Input label="Name *" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Development" />
                <Input label="Description" value={form.description || ''} onChange={(e) => set('description', e.target.value)} />
                <Select label="Color" value={form.color || 'slate'} options={COLOR_OPTIONS} onChange={(e) => set('color', e.target.value)} />
                <Toggle label="Active" checked={form.is_active} onChange={(v) => set('is_active', v)} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button onClick={submit} disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</Button>
            </Modal.Footer>
        </Modal>
    );
}
